// Invite links — one link's story: when it was clicked (vera-2, every click) and what the
// visitor looked at afterwards (PostHog, only visitors analytics may record).
import { Box, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'
import { Skeleton } from '@repo/ui'

import { useTrackedLinkDetail, type InterestSourceState, type TrackedLinkDetail as Detail } from 'hooks/api/useTrackedLinks'
import { Cell, EmptyRow, HeadCell, SectionNote, StatTable, ToneChip, dateTime } from '../InternalAnalyticsPage/components/shared'

const DEVICE_LABELS: Record<string, string> = {
  desktop: 'Desktop',
  mobile: 'Mobile',
  tablet: 'Tablet',
  bot: 'Bot / link preview',
  unknown: 'Unknown',
}

const EVENT_LABELS: Record<string, string> = {
  $pageview: 'Viewed',
  request_access_opened: 'Opened request access',
  request_access_submitted: 'Requested access',
  roi_started: 'Started the ROI calculator',
  roi_calculated: 'Calculated ROI',
  contact_submitted: 'Sent the contact form',
  subscribed: 'Subscribed',
}

export const interestSourceNote = (state: InterestSourceState | undefined): string | null => {
  if (!state) return null
  switch (state.status) {
    case 'ok':
      return null
    case 'fixture':
      return 'Pages viewed are FIXTURE data in this environment (WEBSITE_ANALYTICS_DRIVER=log). Clicks are real.'
    case 'not_configured':
      return `Pages viewed are not available here: ${state.message ?? 'PostHog is not configured'}. Clicks are real.`
    case 'error':
      return `Could not read pages viewed from PostHog (${state.message ?? 'request failed'}). Clicks are real.`
  }
}

/** Human clicks per day as a row of bars: enough to see "clicked twice on the 3rd, again on the 9th". */
const ClickBars = ({ days }: { days: Detail['clicks_by_day'] }) => {
  if (!days.length) return null
  const max = Math.max(...days.map((d) => d.clicks))
  return (
    <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ height: 56 }} aria-label="Clicks per day, last 30 days">
      {days.map((d) => (
        <Box
          key={d.day}
          title={`${d.day}: ${d.clicks} click${d.clicks === 1 ? '' : 's'}`}
          sx={{ width: 10, height: `${Math.max(12, (d.clicks / max) * 100)}%`, borderRadius: 0.5, bgcolor: 'primary.main', opacity: 0.8 }}
        />
      ))}
    </Stack>
  )
}

export const TrackedLinkDetail = ({ id }: { id: string }) => {
  const { data, isLoading, error } = useTrackedLinkDetail(id)
  if (isLoading) return <Skeleton height={14} lines={6} />
  if (error || !data) return <SectionNote>Could not load this link. Reload, or check the API is running.</SectionNote>
  const note = interestSourceNote(data.interestSource)

  return (
    <Stack spacing={4}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <ToneChip tone={data.clicks ? 'positive' : 'neutral'} label={`${data.clicks} click${data.clicks === 1 ? '' : 's'}, all time`} />
        {data.bot_clicks > 0 && <ToneChip tone="neutral" label={`${data.bot_clicks} bot / preview`} />}
        {data.consented_visitors !== null && (
          <ToneChip tone="info" label={`${data.consented_visitors}+ browser${data.consented_visitors === 1 ? '' : 's'} with analytics on`} />
        )}
        {data.top_interest && <ToneChip tone="info" label={`Top interest: ${data.top_interest}`} />}
        {data.archived_at && <ToneChip tone="caution" label="Archived" />}
      </Stack>

      {data.clicks_by_day.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="body3" color="text.secondary">
            Clicks per day, last 30 days
          </Typography>
          <ClickBars days={data.clicks_by_day} />
        </Stack>
      )}

      <Stack spacing={2}>
        <Typography variant="body2">Recent clicks</Typography>
        <StatTable>
          <TableHead>
            <TableRow>
              <HeadCell>When</HeadCell>
              <HeadCell>Device</HeadCell>
              <HeadCell>From</HeadCell>
              <HeadCell>Landed on</HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.recent_clicks.length === 0 ? (
              <EmptyRow colSpan={4} text="Not clicked yet." />
            ) : (
              data.recent_clicks.slice(0, 12).map((c) => (
                <TableRow key={c.clicked_at + c.device}>
                  <Cell>{dateTime(c.clicked_at)}</Cell>
                  <Cell>{DEVICE_LABELS[c.device] ?? c.device}</Cell>
                  <Cell>{c.referrer_host ?? 'Direct or email'}</Cell>
                  <Cell>{c.landing_path}</Cell>
                </TableRow>
              ))
            )}
          </TableBody>
        </StatTable>
      </Stack>

      <Stack spacing={2}>
        <Typography variant="body2">After the click</Typography>
        {note && <SectionNote>{note}</SectionNote>}
        {data.interests && data.interests.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {data.interests.map((i) => (
              <ToneChip key={i.interest} tone="neutral" label={`${i.interest} · ${i.count}`} />
            ))}
          </Stack>
        )}
        {data.pages && (
          <StatTable>
            <TableHead>
              <TableRow>
                <HeadCell>What</HeadCell>
                <HeadCell>Page</HeadCell>
                <HeadCell align="right">Times</HeadCell>
                <HeadCell>Last</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.pages.length === 0 ? (
                <EmptyRow
                  colSpan={4}
                  text="Nothing recorded after the click. Either analytics are off for them, or they have not browsed yet."
                />
              ) : (
                data.pages.slice(0, 20).map((p) => (
                  <TableRow key={`${p.event}:${p.path}`}>
                    <Cell>{EVENT_LABELS[p.event] ?? p.event}</Cell>
                    <Cell>{p.path ?? '—'}</Cell>
                    <Cell align="right">{p.count}</Cell>
                    <Cell>{dateTime(p.last_seen_at)}</Cell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </StatTable>
        )}
        <SectionNote>
          Pages are recorded only while analytics are on for the visitor (they accepted, or in the US did not opt out), never before. Anyone the link is
          forwarded to counts as the person it was made for.
        </SectionNote>
      </Stack>
    </Stack>
  )
}
